import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, Trash2, Minus, FileCheck, Check, RotateCcw, XCircle, Info, FileUp, RefreshCw, FileDown, Copy, Save, Eye, Lock, User, Box, Briefcase, CalendarDays, Pencil, History, GripVertical, ArrowUp, ArrowDown, ArrowUpDown, PackagePlus } from "lucide-react";
import EmptyState from "../components/EmptyState";
import { Button, Spinner } from "../components/ui/kit";
import CenikPicker from "../components/CenikPicker";
import ImportWizard from "../components/ImportWizard";
import PrehledImportu from "../components/PrehledImportu";
import PrepocetPanel from "../components/PrepocetPanel";
import DoCenikuDialog from "../components/DoCenikuDialog";
import { N_STATUS_LABEL, N_TRANSITIONS, nStav } from "../lib/nabidky";
import { fmtKc, fmtDateTime } from "../lib/format";
import { radekCelkem, prodejJednotka, marzeRadku, slevaRadku, nakupZeSlevy, maPevnou, znamyNakup, souctyNabidky, dphOr, validDph,
  ETAPA_RIM, ETAPA_NAZEV } from "../lib/vypocet";
import api, { apiError } from "../lib/api";
import { Drobecky, useNazevStranky } from "../lib/navigace";
import { useJeUzky } from "../lib/mobil";
import DetailHlava, { KartaSekce, Pridat, StitekTmavy } from "../components/DetailHlava";
import { pripravOkno, otevritPdf, zavritOkno, textOkna, TEXT_PRIPRAVA, dlouhoCeka } from "../lib/pdf";
import PdfPripraveno, { PdfCeka } from "../components/PdfPripraveno";
import { PdfNahledTlacitko } from "../components/PdfNahled";
import { Okno, CilNabidkyDialog, NazevSablonyDialog, ZmenyCen } from "../components/SablonyDialogy";
import Vyber from "../components/Vyber";
import { useAuth } from "../context/AuthContext";
import { canSee } from "../lib/nav";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { typ: "MATERIAL", label: "Materiál", key: "mezisoucet_material" },
  { typ: "PRACE", label: "Práce", key: "mezisoucet_prace" },
  { typ: "OSTATNI", label: "Ostatní", key: "mezisoucet_ostatni" },
];
const DPH_HELP = "12 % platí pro montážní práce a materiál u dokončených staveb pro bydlení (§ 48 zákona o DPH). Samotná dodávka zboží bez montáže má 21 %. 0 % použij u přenesené daňové povinnosti (§ 92e) nebo plnění mimo tuzemsko; jinou sazbu (např. 19 % SK) zadej do pole „jiná“.";

// Bez sloupce DPH: sazba je jedna pro celou nabídku (Pod čarou), výjimka se
// nastaví v akcích řádku a svítí oranžově. „E" = s prvním sloupcem etapy.
// Nákup · Prodej · Sleva · Marže — sleva z prodeje dolů na nákup, marže z nákupu nahoru na prodej.
const COLS = "grid-cols-[1fr_52px_78px_100px_84px_100px_92px_100px]";
const COLS_E = "grid-cols-[28px_1fr_52px_78px_100px_84px_100px_92px_100px]";
// Subdodavatel: bez sloupců Nákup a Marže (server mu je neposílá).
const COLS_SUB = "grid-cols-[1fr_52px_84px_92px_100px]";
// Pole, ze kterých nákup a prodej dopočítá server (sladit_ceny) — řádek se pak načte znovu.
const SLEVY_POLE = ["cena_cenik", "rabat_pct", "sleva_pct"];
const pctText = (v) => String(Math.round(Number(v) * 10) / 10).replace(".", ",");
const COLS_SUB_E = "grid-cols-[28px_1fr_52px_84px_92px_100px]";


// Sazba DPH: předvolby + volné číslo (0 % = přenesená daň. povinnost,
// 19 % = slovenská snížená sazba, sazby se v čase mění).
const DPH_PREDVOLBY = [0, 12, 21];
const ETAPA_BARVA = ["#8b8f96", "#3a6fd8", "#d07a1d", "#7d5bd0"];

function DphPicker({ value, onChange, disabled, testid, compact = true }) {
  const v = validDph(value) ? Number(value) : 21;
  const jina = !DPH_PREDVOLBY.includes(v);
  return (
    <div className="flex items-center gap-1" data-testid={testid}>
      <select disabled={disabled} value={jina ? "JINA" : v} aria-label="Sazba DPH"
        onChange={(e) => onChange(e.target.value === "JINA" ? v : Number(e.target.value))}
        className={`${compact ? "text-[11px] h-7 w-[44px] !bg-none !px-1 text-center" : "text-[16px] md:text-[12px] px-2 h-11 md:h-8"} font-600 bg-inputbg border border-line-strong rounded outline-none disabled:bg-transparent disabled:border-transparent`}>
        {DPH_PREDVOLBY.map((p) => <option key={p} value={p}>{p} %</option>)}
        <option value="JINA">jiná…</option>
      </select>
      {jina && (
        <input type="number" min={0} max={100} disabled={disabled} value={v}
          onChange={(e) => { const n = Number(e.target.value); if (e.target.value !== "" && validDph(n)) onChange(n); }}
          className={`amount text-right bg-inputbg border border-line-strong rounded px-1 outline-none ${compact ? "w-12 text-[11px] h-7" : "w-16 text-[16px] md:text-[12px] h-11 md:h-8"}`}
          data-testid={testid ? `${testid}-jina` : undefined} />
      )}
    </div>
  );
}

/** Číselné pole, které se přepočítává až po dopsání.

    Řízený `<input type="number">` s `Number(e.target.value) || 0` je v editoru
    k nepoužití: smazání číslice hodnotu okamžitě přepíše na 0 a zaokrouhlení
    odvozené hodnoty vrací kurzor. Dokud se v poli píše, drží si vlastní text;
    ven se pošle až při opuštění pole nebo Enteru. */
function CisloInput({ value, onCommit, onFlush, disabled, testid, velke = false, placeholder }) {
  const [text, setText] = useState(null);
  const commit = () => {
    if (text !== null) {
      const v = Number(String(text).replace(",", "."));
      setText(null);
      if (Number.isFinite(v)) onCommit(v);
    }
    onFlush && onFlush();
  };
  return (
    <input disabled={disabled} type="number" inputMode="decimal"
      className={`amount bg-inputbg outline-none disabled:bg-transparent ${velke
        ? "w-full min-w-0 text-[16px] text-right rounded-[10px] px-2.5 h-11 disabled:px-0"
        : "w-full min-w-0 text-[12px] text-right rounded px-1.5 h-7"}`}
      value={text === null ? value : text} placeholder={placeholder}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      data-testid={testid} />
  );
}

/** Procento zadané rukou (marže, sleva), i s desetinami („42,9“). Textové pole,
    ne number — to by česká desetinná čárka v některých prohlížečích nepustila.
    Uloží se samo chvilku po dopsání (bez klikání vedle), Enterem hned.
    POZOR: dokud se píše, pole drží text uživatele — průběžné uložení ho nepřepíše
    (smazané „20“ se nesmí vrátit, „42,“ nesmí ztratit čárku). Prázdné pole se
    neukládá; po opuštění se vrátí hodnota řádku. */
function MarzeInput({ value, onCommit, disabled, testid, velke = false, uzke = false, min = -100, max = 10000, popisek = "Marže v procentech" }) {
  const [text, setText] = useState(null);
  const casovac = useRef(null);
  const odeslano = useRef(null);      // text, který už průběžně odešel
  const zobraz = value == null ? "" : String(Math.round(value * 10) / 10).replace(".", ",");
  const hodnota = (t) => {
    const s = String(t ?? "").replace(/\s/g, "");
    if (s === "" || s === "-" || /[.,]$/.test(s)) return null;      // ještě nedopsané
    const v = Number(s.replace(",", "."));
    return Number.isFinite(v) && v >= min && v < max ? v : null;
  };
  const odeslat = (t) => {
    const v = hodnota(t);
    if (v !== null && odeslano.current !== t) { odeslano.current = t; onCommit(v); }
  };
  const commit = () => {
    clearTimeout(casovac.current);
    if (text !== null) odeslat(text);
    odeslano.current = null;
    setText(null);
  };
  useEffect(() => () => clearTimeout(casovac.current), []);
  const zmena = (t) => {
    setText(t);
    clearTimeout(casovac.current);
    casovac.current = setTimeout(() => odeslat(t), 900);
  };
  return (
    <input disabled={disabled} type="text" inputMode="decimal" aria-label={popisek}
      className={`amount bg-inputbg outline-none text-center disabled:bg-transparent ${velke
        ? `${uzke ? "w-[50px]" : "w-[64px]"} text-[16px] rounded-[10px] h-11` : "w-[40px] text-[12px] rounded h-7"}`}
      value={text === null ? zobraz : text}
      onChange={(e) => zmena(e.target.value.replace(/[^\d.,-]/g, ""))}
      onBlur={() => commit()} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      data-testid={testid} />
  );
}

/** Procento s tlačítky −/+ (sleva, marže): na počítači malé, na telefonu velké. */
function Krokovac({ value, lze, editable, onStep, onCommit, min, max, popisek, testid, velke = false }) {
  const text = value == null ? "—" : `${String(Math.round(value * 10) / 10).replace(".", ",")}%`;
  if (!editable || !lze) return <span className={`amount text-center ${velke ? "t-telo font-700 min-w-[44px]" : "text-[12px] min-w-[44px]"}`} data-testid={`${testid}-${"hodnota"}`}>{text}</span>;
  const tl = (d, Ikona, nazev) => (
    <button type="button" onClick={() => onStep(d)} aria-label={nazev} data-testid={`${testid}-${d < 0 ? "minus" : "plus"}`}
      className={velke ? "tap h-11 w-10 shrink-0 rounded-full bg-inputbg text-sec flex items-center justify-center"
        : "h-6 w-[22px] shrink-0 rounded border border-line-strong text-muted hover:text-ink flex items-center justify-center"}>
      <Ikona size={velke ? 15 : 12} /></button>
  );
  return (
    <span className="inline-flex items-center gap-0.5">
      {tl(-1, Minus, `Snížit: ${popisek}`)}
      <span className={`inline-flex items-center ${velke ? "gap-0.5 mx-0.5" : ""}`}>
        <MarzeInput velke={velke} uzke={velke} value={value} min={min} max={max} popisek={popisek} onCommit={onCommit} testid={`${testid}-input`} />
        <span className={velke ? "t-meta text-muted" : "text-[11px] text-muted"}>%</span>
      </span>
      {tl(1, Plus, `Zvýšit: ${popisek}`)}
    </span>
  );
}

/** Štítek etapy u položky: klepnutím — → I → II → III → —. */
function EtapaPill({ etapa, onClick, disabled, testid, velky = false }) {
  const e = Number(etapa) || 0;
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
      title={disabled ? ETAPA_NAZEV[e] : `${ETAPA_NAZEV[e]} — klepnutím změníš`}
      className={`${velky ? "tap h-11 min-w-[44px]" : "h-[26px] min-w-[26px]"} px-1.5 shrink-0 rounded-[8px] t-meta font-800 inline-flex items-center justify-center disabled:cursor-default`}
      style={e ? { background: `${ETAPA_BARVA[e]}1f`, color: ETAPA_BARVA[e] } : { background: "#f1f0ed", color: "#8b8f96" }}>
      {ETAPA_RIM[e]}
    </button>
  );
}

/** Odkud je prodejní cena: cena pro klienta / z ceníku / bez nákupu. */
function CenaPuvod({ r, skryt }) {
  if (r.sleva_pct && r.cena_cenik != null) return (
    <span className="inline-flex flex-wrap items-center gap-x-1.5 text-[#2c6ecb]" data-testid={`radek-sleva-${r.id}`}>
      <span className="font-700">pro klienta −{pctText(r.sleva_pct)} %</span><span className="text-muted">z ceníkové ceny {fmtKc(r.cena_cenik)}</span>
    </span>);
  if (maPevnou(r)) return (
    <span className="inline-flex items-center gap-1 text-[#2c6ecb]" data-testid={`radek-pevna-${r.id}`}><User size={11} />cena pro klienta
      {/* Nákup neznámý (generální dodavatel ho neřekl) — marže 0, dokud se nedoplní. */}
      {!skryt && !znamyNakup(r) && <span className="text-[#9a5b00]" data-testid={`radek-bez-nakupu-${r.id}`}>· nákup chybí</span>}
    </span>);
  if (skryt) return null;
  if (!znamyNakup(r)) return <span className="text-muted">bez nákupu</span>;
  if (r.polozka_id) return <span className="text-muted">z ceníku</span>;
  return null;
}

// Řádek v režimu pořadí na telefonu: jen název a šipky (tahat prstem mezi poli nejde spolehlivě).
function RadekPoradi({ r, prvni, posledni, onPosun }) {
  const tl = (smer, Ikona, popis, zakaz) => (
    <button type="button" onClick={() => onPosun(r.id, smer)} disabled={zakaz} aria-label={popis}
      className="tap h-11 w-11 shrink-0 rounded-full bg-inputbg text-sec flex items-center justify-center disabled:opacity-30"
      data-testid={`radek-${smer < 0 ? "nahoru" : "dolu"}-${r.id}`}><Ikona size={16} /></button>
  );
  return (
    <div className="flex items-center gap-2 px-4 py-2 border-t border-line-soft" data-testid={`radek-${r.id}`}>
      <span className="flex-1 min-w-0 t-telo font-600 text-ink truncate">{r.nazev}</span>
      {tl(-1, ArrowUp, "Posunout výš", prvni)}
      {tl(1, ArrowDown, "Posunout níž", posledni)}
    </div>
  );
}

function Row({ r, editable, onChange, onFlush, onDelete, skryt = false, mobil = false, etapy = false, dphNabidky = 21,
  vybrano = false, onVyber, vybirani = false, tazeni = null }) {
  const [local, setLocal] = useState(r);
  // Řádek jde táhnout jen za úchyt — jinak by tažení bralo výběr textu v polích.
  const [chyceno, setChyceno] = useState(false);
  useEffect(() => setLocal(r), [r.id]); // resync only on remount (after structural load)
  // Ruční marže nebo prodej ruší slevu pro klienta (server: sladit_ceny) — popisek pod položkou hned zmizí.
  const emit = (patch, delay) => {
    const rusiSlevu = "marze_pct" in patch || "cena_prodej" in patch;
    setLocal((l) => ({ ...l, ...patch, ...(rusiSlevu && l.sleva_pct ? { sleva_pct: null } : {}) }));
    onChange(r.id, patch, delay);
  };
  const celkem = radekCelkem(local);
  const prodej = prodejJednotka(local);
  const marze = marzeRadku(local);
  const sleva = slevaRadku(local);
  // Marži jde krokovat jen se známým nákupem — bez něj by prodej spadl na 0.
  const lzeMarze = znamyNakup(local) && Number(local.cena_nakup) > 0;
  const marzeStep = (delta) => {
    if (!lzeMarze) return;
    const nm = Math.max(0, Math.round(((marze ?? 0) + delta) * 10) / 10);
    emit({ marze_pct: nm, cena_prodej: null }, 300);   // změna marže = pevná cena padá
  };
  // Prodejní cena přepsaná rukou = cena pro klienta. Drží se i při změně
  // nákupu a přepočtu ceníku; marže se z ní jen dopočítá.
  const ulozProdej = (v) => { if (v >= 0) emit({ cena_prodej: v }, 0); };
  // Změna nákupu nechá prodej pro klienta, jak byl, a marže se dopočítá
  // (prodej se tím stane cenou pro klienta). Celou nabídku na nové nákupky
  // přepočítá „Přepočítat na dnešní ceník“.
  const ulozNakup = (v) => {
    const drzetProdej = !maPevnou(local) && Number(local.cena_nakup) > 0 && prodej > 0 && v !== Number(local.cena_nakup);
    emit(drzetProdej ? { cena_nakup: v, cena_prodej: prodej } : { cena_nakup: v }, 0);
  };
  const ulozMarzi = (v) => { if (lzeMarze) emit({ marze_pct: v, cena_prodej: null }, 0); };
  // Sleva z prodeje: prodej zůstane (pevná cena), nákup = prodej × (1 − sleva).
  // Jde i u řádku bez nákupu — tak se nákup vytvoří.
  const lzeSlevu = prodej > 0;
  const ulozSlevu = (v, delay = 0) => { if (lzeSlevu) emit({ cena_nakup: nakupZeSlevy(prodej, v), cena_prodej: prodej }, delay); };
  const slevaStep = (delta) => ulozSlevu(Math.min(95, Math.max(0, Math.round(((sleva ?? 0) + delta) * 10) / 10)), 300);
  const slevaPole = (velke) => (
    <Krokovac value={sleva} lze={lzeSlevu} editable={editable} onStep={slevaStep} onCommit={(v) => ulozSlevu(v)}
      min={0} max={100} popisek="sleva z prodeje v %" testid={`radek-sleva-${r.id}`} velke={velke} />);
  const marzePole = (velke) => (
    <Krokovac value={marze} lze={lzeMarze} editable={editable} onStep={marzeStep} onCommit={ulozMarzi}
      min={-100} max={10000} popisek="marže v %" testid={`radek-marze-${r.id}`} velke={velke} />);
  const dalsiEtapa = () => emit({ etapa: ((Number(local.etapa) || 0) + 1) % 4 }, 0);
  const jinaDph = dphOr(local.dph_sazba_pct, dphNabidky) !== dphNabidky;
  const puvod = <CenaPuvod r={local} skryt={skryt} />;

  if (mobil) {
    const pole = (popisek, prvek) => (
      <label className="block min-w-0"><span className="label-up text-muted block mb-1">{popisek}</span>{prvek}</label>
    );
    return (
      <div className="px-4 py-3 border-t border-line-soft" data-testid={`radek-${r.id}`}>
        <div className="flex items-start gap-3">
          {editable && vybirani && onVyber && <input type="checkbox" checked={vybrano} onChange={() => onVyber(r.id)} aria-label="Označit řádek"
            className="h-[22px] w-[22px] mt-2.5 shrink-0 accent-ink" data-testid={`radek-vyber-${r.id}`} />}
          {etapy && <EtapaPill etapa={local.etapa} velky disabled={!editable} onClick={dalsiEtapa} testid={`radek-etapa-${r.id}`} />}
          <div className="flex-1 min-w-0">
            <input disabled={!editable} className="w-full min-w-0 bg-transparent text-[16px] font-600 outline-none disabled:text-ink"
              value={local.nazev} onChange={(e) => emit({ nazev: e.target.value })} onBlur={() => onFlush(r.id)} />
            <span className="block t-meta">{puvod}</span>
          </div>
          <span className="amount t-telo font-800 text-ink whitespace-nowrap pt-0.5" data-testid={`radek-celkem-${r.id}`}>{fmtKc(celkem)} Kč</span>
          {editable && <button onClick={() => onDelete(r.id)} aria-label="Smazat řádek" className="tap -my-2.5 h-11 w-11 shrink-0 flex items-center justify-center text-muted hover:text-danger" data-testid={`radek-del-${r.id}`}><Trash2 size={16} /></button>}
        </div>
        <div className={`mt-2 grid ${skryt ? "grid-cols-2" : "grid-cols-3"} gap-2`}>
          {pole("Množ.", <CisloInput velke disabled={!editable} value={local.mnozstvi} testid={`radek-mnozstvi-${r.id}`}
            onCommit={(v) => emit({ mnozstvi: v }, 0)} onFlush={() => onFlush(r.id)} />)}
          {skryt
            ? pole("Prodej", <CisloInput velke disabled={!editable || !!r.polozka_id} value={local.cena_nakup ?? ""} testid={`radek-prodej-${r.id}`}
                onCommit={(v) => emit({ cena_nakup: v }, 0)} onFlush={() => onFlush(r.id)} />)
            : <>
              {pole("Nákup", <CisloInput velke disabled={!editable} value={local.cena_nakup ?? ""} placeholder="—" testid={`radek-nakup-${r.id}`}
                onCommit={ulozNakup} onFlush={() => onFlush(r.id)} />)}
              {pole("Prodej", <CisloInput velke disabled={!editable} value={prodej} testid={`radek-prodej-${r.id}`}
                onCommit={ulozProdej} onFlush={() => onFlush(r.id)} />)}
            </>}
        </div>
        {!skryt && (
          <div className="mt-2 grid grid-cols-2 gap-2">
            <div className="min-w-0"><span className="label-up text-muted block mb-1">Sleva</span>{slevaPole(true)}</div>
            <div className="min-w-0"><span className="label-up text-muted block mb-1">Marže</span>{marzePole(true)}</div>
          </div>
        )}
        <div className="mt-2 flex items-center gap-1.5">
          <span className={jinaDph ? "rounded ring-2 ring-[#f0a35a]" : ""} title={jinaDph ? "Jiná sazba než nabídka" : "Sazba DPH"}>
            <DphPicker value={local.dph_sazba_pct} disabled={!editable} testid={`radek-dph-${r.id}`} compact={false}
              onChange={(v) => emit({ dph_sazba_pct: v }, 150)} />
          </span>
          {editable && (
            <select value={local.typ} onChange={(e) => emit({ typ: e.target.value }, 0)} title="Přesunout do jiné sekce" aria-label="Sekce"
              className="ml-auto min-w-0 text-[16px] font-600 bg-transparent border border-line-strong rounded-[10px] px-1 h-11 outline-none text-muted"
              data-testid={`radek-typ-${r.id}`}>
              <option value="MATERIAL">Mat.</option>
              <option value="PRACE">Práce</option>
              <option value="OSTATNI">Ost.</option>
            </select>
          )}
        </div>
      </div>
    );
  }
  const cols = skryt ? (etapy ? COLS_SUB_E : COLS_SUB) : (etapy ? COLS_E : COLS);
  return (
    <div className={`grid ${cols} gap-2 items-center px-3 md:px-5 py-2 border-t ${tazeni?.cil === r.id && tazeni.tazeny !== r.id ? "border-t-2 border-t-accent" : "border-line-soft"} ${tazeni?.tazeny === r.id ? "opacity-40" : ""}`}
      data-testid={`radek-${r.id}`} draggable={!!tazeni && chyceno}
      onDragStart={tazeni ? (e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", r.id); tazeni.zacni(r.id); } : undefined}
      onDragOver={tazeni ? (e) => { e.preventDefault(); tazeni.nad(r.id); } : undefined}
      onDrop={tazeni ? (e) => { e.preventDefault(); tazeni.pust(r.id); } : undefined}
      onDragEnd={tazeni ? () => { setChyceno(false); tazeni.konec(); } : undefined}>
      {etapy && <EtapaPill etapa={local.etapa} disabled={!editable} onClick={dalsiEtapa} testid={`radek-etapa-${r.id}`} />}
      <div className="min-w-0 flex items-start gap-2">
        {tazeni && (
          <span onMouseDown={() => setChyceno(true)} onMouseUp={() => setChyceno(false)} title="Přetáhni pro změnu pořadí"
            className="-ml-2 mt-0.5 shrink-0 cursor-grab active:cursor-grabbing text-muted-2 hover:text-ink" data-testid={`radek-uchyt-${r.id}`}>
            <GripVertical size={14} />
          </span>
        )}
        {editable && onVyber && <input type="checkbox" checked={vybrano} onChange={() => onVyber(r.id)} aria-label="Označit řádek"
          className="h-4 w-4 mt-0.5 shrink-0 accent-ink" data-testid={`radek-vyber-${r.id}`} />}
        <div className="min-w-0 flex-1">
          <input disabled={!editable} className="w-full bg-transparent text-[12.5px] font-600 outline-none disabled:text-ink"
            value={local.nazev} onChange={(e) => emit({ nazev: e.target.value })} onBlur={() => onFlush(r.id)} />
          <span className="block text-[10.5px] leading-tight">{puvod}</span>
        </div>
      </div>
      <CisloInput disabled={!editable} value={local.mnozstvi} testid={`radek-mnozstvi-${r.id}`}
        onCommit={(v) => emit({ mnozstvi: v }, 0)} onFlush={() => onFlush(r.id)} />
      {skryt ? (
        // Prodej: u položky z ceníku ji drží hostitel, u vlastního řádku ji zadá subdodavatel.
        <CisloInput disabled={!editable || !!r.polozka_id} value={local.cena_nakup ?? ""} testid={`radek-prodej-${r.id}`}
          onCommit={(v) => emit({ cena_nakup: v }, 0)} onFlush={() => onFlush(r.id)} />
      ) : (<>
      <CisloInput disabled={!editable} value={local.cena_nakup ?? ""} placeholder="—" testid={`radek-nakup-${r.id}`}
        onCommit={ulozNakup} onFlush={() => onFlush(r.id)} />
      <div className="flex justify-center">{slevaPole(false)}</div>
      <CisloInput disabled={!editable} value={prodej} testid={`radek-prodej-${r.id}`}
        onCommit={ulozProdej} onFlush={() => onFlush(r.id)} />
      </>)}
      {!skryt && <div className="flex justify-end">{marzePole(false)}</div>}
      <span className="amount text-[12.5px] font-700 text-ink text-right" data-testid={`radek-celkem-${r.id}`}>{fmtKc(celkem)} Kč</span>
      <div className="flex items-center gap-1 justify-self-end">
        <span className={jinaDph ? "rounded ring-2 ring-[#f0a35a]" : ""} title={jinaDph ? "Jiná sazba než nabídka" : "Sazba DPH"}>
          <DphPicker value={local.dph_sazba_pct} disabled={!editable} testid={`radek-dph-${r.id}`}
            onChange={(v) => emit({ dph_sazba_pct: v }, 150)} />
        </span>
        {editable && <>
          <select value={local.typ} onChange={(e) => emit({ typ: e.target.value }, 0)}
            title="Přesunout do jiné sekce"
            className="text-[10px] font-600 bg-transparent border border-line-strong rounded px-0.5 h-6 w-[28px] !bg-none !px-0 text-center outline-none text-muted"
            data-testid={`radek-typ-${r.id}`}>
            <option value="MATERIAL">M</option>
            <option value="PRACE">P</option>
            <option value="OSTATNI">O</option>
          </select>
          <button onClick={() => onDelete(r.id)} className="text-muted hover:text-danger" data-testid={`radek-del-${r.id}`}><Trash2 size={14} /></button>
        </>}
      </div>
    </div>
  );
}

// Proč jsou položky uzamčené — podle stavu, ať hláška neslibuje, co nejde.
const ZAMCENO = {
  ODESLANA: "Nabídka je dokončená a uzamčená. Čeká na rozhodnutí klienta: schval ji, zamítni, nebo ji vrať na rozpracovanou.",
  SCHVALENA: "Nabídka je schválená, je to objednávka klienta. Položky už nejdou měnit.",
  ZAMITNUTA: "Nabídka je zamítnutá. Vrať ji na rozpracovanou a uprav, připrav jinou variantu, nebo technologii v případu označ jako ztracenou.",
  NAHRAZENA: "Tuto verzi nahradila novější schválená nabídka.",
};

export default function NabidkaEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [meta, setMeta] = useState(undefined); // {nabidka, kontext, editable, kategorie_id}
  const [verze, setVerze] = useState(null);
  const [rows, setRows] = useState([]);
  const [loadKey, setLoadKey] = useState(0);
  const [picker, setPicker] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [viewVerze, setViewVerze] = useState(null);   // null = aktuální verze
  const [recalcOpen, setRecalcOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfOdkaz, setPdfOdkaz] = useState(null);   // hotové PDF, když prohlížeč nepovolil záložku
  const [pdfCeka, setPdfCeka] = useState(false);   // po aktualizaci se tisk PDF chvíli zprovozňuje
  const [sablonyDlg, setSablonyDlg] = useState(null);   // {typ: "sablona"|"kopie"|"kopieHotovo", ...}
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [saveState, setSaveState] = useState("idle"); // idle|saving|saved
  const [showHelp, setShowHelp] = useState(false);
  const [etapyZap, setEtapyZap] = useState(false);   // „Rozdělit na etapy" před prvním přiřazením
  const [filtrEtapa, setFiltrEtapa] = useState(0);   // 0 = celá nabídka
  // Na tabletu se tabulka položek (11 sloupců) nevejde — řádky jako karty jako na telefonu.
  const uzky = useJeUzky();
  // Hromadná úprava: označené řádky (id → true), na telefonu/tabletu se označuje po „Vybrat“.
  const [vybrane, setVybrane] = useState({});
  // Okno „Uložit ceny do ceníku“: { ids?: vybrané řádky, dokonceni?: dotaz před Dokončit }.
  const [doCeniku, setDoCeniku] = useState(null);
  const [vybirani, setVybirani] = useState(false);
  // Pořadí řádků: tažení na PC ({tazeny, cil}), na telefonu režim se šipkami.
  const [tah, setTah] = useState(null);
  const [razeni, setRazeni] = useState(false);
  const [hromadnaMarze, setHromadnaMarze] = useState("");
  const [hromadnaSleva, setHromadnaSleva] = useState("");
  // Sloupce Ceník · Rabat · Sleva: zapamatované v prohlížeči, samy se zapnou, když je řádek má.
  const [upravNazev, setUpravNazev] = useState(false);
  const [nazevText, setNazevText] = useState("");
  const [historie, setHistorie] = useState(null);   // null | {items, detail}

  const timers = useRef({});
  const pending = useRef({});
  const seqRef = useRef({});
  const dopravaTimer = useRef(null);
  const slevaTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const q = viewVerze ? `?verze=${viewVerze}` : "";
      const { data } = await api.get(`/api/nabidky/${id}${q}`);
      setMeta({ nabidka: data.nabidka, kontext: data.kontext, editable: data.editable, kategorie_id: data.kategorie_id,
        verze_seznam: data.verze_seznam || [], aktualni_verze: data.aktualni_verze, je_aktualni: data.je_aktualni,
        skryt_nakup: !!data.skryt_nakup, smi_spravovat: data.smi_spravovat !== false, pdf: data.pdf,
        varianty: data.varianty || {} });
      setVerze(data.verze);
      setRows(data.totals.rows);
      setLoadKey((k) => k + 1);
    } catch { setMeta(null); }
  }, [id, viewVerze]);
  useEffect(() => { load(); }, [load]);
  useNazevStranky(meta?.nabidka && `Nabídka ${meta.nabidka.cislo}`);

  // flush any pending debounced saves when leaving the editor
  useEffect(() => () => {
    Object.entries(pending.current).forEach(([rid, patch]) => {
      if (patch) api.patch(`/api/nabidka-polozky/${rid}`, patch).catch(() => {});
    });
  }, []);

  // Jen stav uloženého PDF (po exportu a po každém uložení změn) — bez přenačtení položek.
  const nactiPdf = useCallback(async () => {
    try {
      const q = viewVerze ? `?verze=${viewVerze}` : "";
      const { data } = await api.get(`/api/nabidky/${id}${q}`);
      setMeta((m) => (m ? { ...m, pdf: data.pdf } : m));
    } catch { /* náhled PDF počká na další načtení */ }
  }, [id, viewVerze]);
  useEffect(() => { if (saveState === "saved") nactiPdf(); }, [saveState, nactiPdf]);

  if (meta === undefined) return <div className="p-10 flex justify-center text-muted"><Spinner /></div>;
  if (meta === null) return <div className="card p-8"><EmptyState icon="FileX" title="Nabídka nenalezena" action={<Button variant="ghost" onClick={() => nav("/nabidky")}>Zpět</Button>} /></div>;

  const { nabidka, kontext, editable, kategorie_id, skryt_nakup: skryt = false, smi_spravovat: spravce = true } = meta;
  const { verze_seznam = [], je_aktualni = true } = meta;
  const vid = verze.id;
  const prenesena = verze.rezim_dph === "PRENESENA_POVINNOST";
  const defaultSazba = prenesena ? 0 : dphOr(verze.dph_sazba_pct, 21);
  const totals = souctyNabidky(rows, verze.doprava, defaultSazba, verze.sleva);
  const sEtapami = etapyZap || rows.some((r) => Number(r.etapa) > 0);
  const maxEtapa = Math.max(2, ...rows.map((r) => Number(r.etapa) || 0));

  const markSaving = () => setSaveState("saving");
  const markSaved = () => { setSaveState("saved"); setTimeout(() => setSaveState((s) => (s === "saved" ? "idle" : s)), 1400); };

  const patchLocal = (rid, patch) => setRows((rs) => rs.map((r) => (r.id === rid ? { ...r, ...patch } : r)));

  const flushRow = async (rid) => {
    clearTimeout(timers.current[rid]);
    const patch = pending.current[rid];
    if (!patch) return;
    pending.current[rid] = null;
    const my = (seqRef.current[rid] = (seqRef.current[rid] || 0) + 1);
    try { await api.patch(`/api/nabidka-polozky/${rid}`, patch); if (seqRef.current[rid] === my) markSaved(); }
    catch (e) { setErr(apiError(e.response?.data?.detail)); }
  };
  const onRowChange = (rid, patch, delay = 400) => {
    patchLocal(rid, patch);
    // změna typu mění zařazení do sekce -> po uložení překreslit strukturu
    if (patch.typ) { pending.current[rid] = { ...(pending.current[rid] || {}), ...patch };
      flushRow(rid).then(() => setLoadKey((k) => k + 1)); return; }
    // Ceník, rabat, sleva: nákup a prodej dopočítá server — uložit hned a řádky načíst znovu.
    if (SLEVY_POLE.some((k) => k in patch)) { pending.current[rid] = { ...(pending.current[rid] || {}), ...patch };
      markSaving(); flushRow(rid).then(load).then(markSaved); return; }
    pending.current[rid] = { ...(pending.current[rid] || {}), ...patch };
    clearTimeout(timers.current[rid]);
    markSaving();
    timers.current[rid] = setTimeout(() => flushRow(rid), delay);
  };

  const delRow = async (rid) => { await api.delete(`/api/nabidka-polozky/${rid}`); load(); };

  // Nové pořadí v rámci sekce; stejné pořadí má i PDF. Uloží se celé (všechny sekce).
  const ulozPoradi = async (typ, presunId, naId, za = false) => {
    const sekce = rows.filter((r) => r.typ === typ);
    const z = sekce.findIndex((r) => r.id === presunId);
    if (z < 0 || presunId === naId) return;
    const nova = sekce.filter((r) => r.id !== presunId);
    const kam = nova.findIndex((r) => r.id === naId);
    if (kam < 0) return;
    nova.splice(kam + (za ? 1 : 0), 0, sekce[z]);
    let i = 0;
    const vse = rows.map((r) => (r.typ === typ ? nova[i++] : r));
    setRows(vse); markSaving();
    try { await api.post(`/api/nabidky-verze/${vid}/poradi`, { ids: vse.map((r) => r.id) }); markSaved(); }
    catch (e) { setErr(apiError(e.response?.data?.detail)); setSaveState("idle"); load(); }
  };
  const posun = (typ) => (rid, smer) => {
    const sekce = rows.filter((r) => r.typ === typ);
    const i = sekce.findIndex((r) => r.id === rid);
    const soused = sekce[i + smer];
    if (soused) ulozPoradi(typ, rid, soused.id, smer > 0);
  };
  const tazeniSekce = (typ) => ({
    tazeny: tah?.tazeny, cil: tah?.cil,
    zacni: (rid) => setTah({ tazeny: rid, cil: null }),
    nad: (rid) => setTah((t) => (t && t.cil !== rid ? { ...t, cil: rid } : t)),
    pust: (rid) => {
      const t = tah; setTah(null);
      if (!t || t.tazeny === rid || rows.find((r) => r.id === t.tazeny)?.typ !== typ) return;
      // Táhne-li se dolů, řádek padne za cíl; nahoru před něj.
      const sekce = rows.filter((r) => r.typ === typ);
      ulozPoradi(typ, t.tazeny, rid, sekce.findIndex((r) => r.id === t.tazeny) < sekce.findIndex((r) => r.id === rid));
    },
    konec: () => setTah(null),
  });

  const exportPdf = async (sCenami) => {
    // Záložka se otevře hned při kliknutí — později by ji prohlížeč zablokoval (lib/pdf.js).
    const okno = pripravOkno();
    setPdfBusy(true); setErr(""); setPdfOdkaz(null);
    const chyba = (text) => { zavritOkno(okno); setPdfBusy(false); setErr(text); };
    try {
      const { data } = await api.post(`/api/nabidky/${nabidka.id}/pdf`, { verze: verze.verze, s_cenami_polozek: sCenami });
      const zacatek = Date.now();
      let hlaseno = false;
      const poll = setInterval(async () => {
        try {
          const r = await api.get(`/api/pdf-jobs/${data.job_id}`);
          if (r.data.status === "processing" && !hlaseno && dlouhoCeka(r.data, zacatek)) {
            hlaseno = true; setPdfCeka(true); textOkna(okno, TEXT_PRIPRAVA);
          }
          if (r.data.status !== "processing") setPdfCeka(false);
          if (r.data.status === "done") {
            clearInterval(poll); setPdfBusy(false);
            const url = `${API_BASE}${r.data.result.url}`;
            if (!otevritPdf(okno, url)) setPdfOdkaz(url);
            nactiPdf();   // uložené PDF se hned ukáže v „Náhled PDF"
          }
          else if (r.data.status === "error") { clearInterval(poll); chyba(r.data.detail || "PDF se nepodařilo vytvořit."); }
        } catch (e) { clearInterval(poll); setPdfCeka(false); chyba(apiError(e.response?.data?.detail)); }
      }, 1500);
    } catch (e) { chyba(apiError(e.response?.data?.detail)); }
  };
  const vybraneIds = Object.keys(vybrane).filter((k) => vybrane[k]);
  const prepniVyber = (rid) => setVybrane((v) => ({ ...v, [rid]: !v[rid] }));
  const hromadne = async (zmena) => {
    if (!vybraneIds.length) return;
    setBusy(true); setErr(""); markSaving();
    try {
      await Promise.all(Object.keys(pending.current).map((rid) => flushRow(rid)));
      await api.post(`/api/nabidky-verze/${vid}/polozky/hromadne`, { ids: vybraneIds, ...zmena });
      setVybrane({}); setHromadnaMarze(""); markSaved(); await load();
    } catch (e) { setErr(apiError(e.response?.data?.detail)); setSaveState("idle"); }
    finally { setBusy(false); }
  };
  const nastavSlevu = () => {
    const v = Number(String(hromadnaSleva).replace(/\s/g, "").replace(",", "."));
    // Sleva z prodeje → nákup (prodej zůstane), i u řádků bez nákupu.
    if (hromadnaSleva.trim() !== "" && Number.isFinite(v) && v >= 0 && v < 100) { hromadne({ nakup_sleva_pct: v }); setHromadnaSleva(""); }
  };
  const vlastniMarze = () => {
    const v = Number(String(hromadnaMarze).replace(/\s/g, "").replace(",", "."));
    if (hromadnaMarze.trim() !== "" && Number.isFinite(v)) hromadne({ marze_pct: v });
  };
  // Název nabídky (číslo a datum dává systém) — pro návaznost na zakázku a servis.
  const zacitNazev = () => { setNazevText(nabidka.nazev || ""); setUpravNazev(true); };
  const ulozNazev = async (text) => {
    const n = text.split(/\s+/).filter(Boolean).join(" ");
    setUpravNazev(false);
    if (!n || n === nabidka.nazev) return;
    try {
      await api.patch(`/api/nabidky/${nabidka.id}`, { nazev: n });
      setMeta((m) => ({ ...m, nabidka: { ...m.nabidka, nazev: n } }));
    } catch (e) { setErr(apiError(e.response?.data?.detail)); }
  };
  const otevritHistorii = async () => {
    try { const { data } = await api.get(`/api/nabidky-verze/${vid}/import/davky`); setHistorie({ items: data.items, detail: null }); }
    catch (e) { setErr(apiError(e.response?.data?.detail)); }
  };
  const detailImportu = async (did) => {
    try { const { data } = await api.get(`/api/import-davky/${did}`); setHistorie((h) => ({ ...h, detail: data })); }
    catch (e) { setErr(apiError(e.response?.data?.detail)); }
  };
  const vratitImportZHistorie = async (d) => {
    if (!window.confirm(`Vrátit import ${d.soubor || ""}?\n\nZ nabídky se odeberou jeho položky (${d.radku}). `
      + "Položky, které import založil do ceníku, se smažou a přepsané ceny v ceníku se vrátí.")) return;
    try {
      await api.post(`/api/import-davky/${d.id}/vratit`);
      await load(); await otevritHistorii();
    } catch (e) { setErr(apiError(e.response?.data?.detail)); }
  };

  // Nová položka při filtru etapy patří do té etapy.
  const addBlank = async (typ) => { await api.post(`/api/nabidky-verze/${vid}/polozky`, { typ, nazev: "Nová položka", mnozstvi: 1, cena_nakup: 0, marze_pct: 0, etapa: filtrEtapa }); load(); };
  const addFromCenik = async (p) => {
    const typ = p.typ === "PRACE" ? "PRACE" : p.typ === "DOPRAVA" ? "OSTATNI" : "MATERIAL";
    await api.post(`/api/nabidky-verze/${vid}/polozky`, { typ, polozka_id: p.id, etapa: filtrEtapa });
    setPicker(null); load();
  };

  const patchDoprava = (field, value) => {
    // Na obrazovce zůstane, co uživatel píše (smazané pole nesmí skočit na 0), server dostane číslo.
    const zobraz = { ...(verze.doprava || {}), [field]: field === "dph_sazba_pct" ? Number(value) : value };
    const nd = Object.fromEntries(Object.entries(zobraz).map(([k, x]) =>
      [k, k === "dph_sazba_pct" ? x : (Number(String(x ?? "").replace(",", ".")) || 0)]));
    setVerze((v) => ({ ...v, doprava: zobraz }));
    clearTimeout(dopravaTimer.current);
    markSaving();
    dopravaTimer.current = setTimeout(async () => {
      try { await api.patch(`/api/nabidky-verze/${vid}`, { doprava: nd }); markSaved(); }
      catch (e) { setErr(apiError(e.response?.data?.detail)); }
    }, 400);
  };
  // Jedna DPH pro celou nabídku: server přepíše všechny řádky i dopravu.
  const setDefaultDph = async (v) => {
    markSaving();
    try {
      await Promise.all(Object.keys(pending.current).map((rid) => flushRow(rid)));
      await api.patch(`/api/nabidky-verze/${vid}`, { dph_sazba_pct: v });
      markSaved(); load();
    } catch (e) { setErr(apiError(e.response?.data?.detail)); }
  };
  const patchSleva = (zmena) => {
    const nova = { typ: "KC", hodnota: 0, ...(verze.sleva || {}), ...zmena };
    setVerze((v) => ({ ...v, sleva: nova }));
    clearTimeout(slevaTimer.current);
    markSaving();
    slevaTimer.current = setTimeout(async () => {
      try { await api.patch(`/api/nabidky-verze/${vid}`, { sleva: nova }); markSaved(); }
      catch (e) { setErr(apiError(e.response?.data?.detail)); }
    }, 400);
  };
  const setRezimDph = async (v) => {
    setVerze((prev) => ({ ...prev, rezim_dph: v }));
    markSaving();
    await api.patch(`/api/nabidky-verze/${vid}`, { rezim_dph: v });
    if (v === "PRENESENA_POVINNOST") setRows((rs) => rs.map((r) => ({ ...r, dph_sazba_pct: 0 })));
    markSaved();
    setLoadKey((k) => k + 1);
  };

  // Schválenou (objednávka klienta) server smazat nedovolí — tlačítko se ani neukáže.
  const smazatNabidku = async () => {
    if (!window.confirm(`Smazat nabídku ${nabidka.cislo} i se všemi verzemi?\n\nVrátit to nejde. Technologie se vrátí k nacenění.`)) return;
    setBusy(true); setErr("");
    try {
      await api.delete(`/api/nabidky/${nabidka.id}`);
      nav(`/obchod/${kontext.pripad_id}`, { replace: true });
    } catch (e) { setErr(apiError(e.response?.data?.detail) || e.message); setBusy(false); }
  };

  const jineVarianty = meta.varianty?.jine_zive || 0;
  const changeStatus = async (s) => {
    // Schválení má důsledky, o kterých je potřeba vědět předem.
    if (s === "SCHVALENA" && !window.confirm(
      "Schválit nabídku?\n\n• Technologie přejde do fáze Objednáno.\n• Její hodnota se nastaví podle nabídky.\n"
      + "• Vzniknou úkoly (smlouva, materiál, záloha).\n• Položky nabídky už nepůjde měnit."
      + (jineVarianty ? `\n• Ostatní varianty (${jineVarianty}) se označí jako nevybrané.` : ""))) return;
    // Před dokončením nabídnout uložení nákupů, které v ceníku nejsou (okno se
    // ukáže, jen když je co uložit).
    if (s === "ODESLANA" && lzeDoCeniku) { await otevritDoCeniku({ dokonceni: true }); return; }
    await nastavStav(s);
  };
  // Rozepsané změny řádků nejdřív uložit — návrh se počítá z uložených cen.
  const otevritDoCeniku = async (o) => {
    await Promise.all(Object.keys(pending.current).map((rid) => flushRow(rid)));
    setDoCeniku(o);
  };
  const nastavStav = async (s) => {
    setBusy(true); setErr("");
    try { await api.post(`/api/nabidky-verze/${vid}/status`, { status: s }); await load(); }
    catch (e) { setErr(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  // Schválení vrací jen majitel (omyl, klient ještě nepotvrdil). Odstoupení
  // klienta od objednávky patří do ztráty technologie, ne sem.
  const vratitSchvaleni = async () => {
    const duvod = window.prompt("Proč schválení vracíš? Zapíše se do historie případu.\n\n"
      + "Nabídka se vrátí na Dokončená, ostatní varianty se znovu otevřou a technologie z Objednáno přejde zpět na Nabídku.");
    if (!duvod || !duvod.trim()) return;
    setBusy(true); setErr("");
    try { await api.post(`/api/nabidky-verze/${vid}/vratit-schvaleni`, { duvod: duvod.trim() }); await load(); }
    catch (e) { setErr(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const rowsByType = (typ) => rows.filter((r) => r.typ === typ && (!filtrEtapa || Number(r.etapa) === filtrEtapa));
  // Archivní verze je jen ke čtení — tlačítka by skončila chybou 409.
  // Odeslat a schválit nabídku smí jen hostitel (subdodavatel ji jen připraví).
  // Když je schválená jiná varianta, tuhle schválit ani znovu otevřít nejde (server to odmítne).
  const nextStatuses = (verze.archiv || !je_aktualni || !spravce ? [] : (N_TRANSITIONS[verze.status] || []))
    .filter((st) => !meta.varianty?.schvalena_jina || !["SCHVALENA", "ROZPRACOVANA"].includes(st));
  // Do ceníku zapisuje majitel a obchodník (ne subdodavatel, ten nákup nevidí).
  const lzeDoCeniku = !skryt && canSee(user, ["OWNER", "OBCHODNIK"]) && je_aktualni && !verze.archiv;
  const lzeVratitSchvaleni = canSee(user, ["OWNER"]) && verze.status === "SCHVALENA" && je_aktualni && !verze.archiv;
  // „Dokončit", ne „Odeslat": klientovi se nic neposílá, nabídka se jen uzamkne.
  const statusBtn = { ODESLANA: { icon: FileCheck, label: "Dokončit nabídku" }, SCHVALENA: { icon: Check, label: "Schválit" },
    ZAMITNUTA: { icon: XCircle, label: "Zamítnout" }, ROZPRACOVANA: { icon: RotateCcw, label: "Zpět na rozpracovanou" } };

  const B_STAV = { ROZPRACOVANA: "#ffcf7a", ODESLANA: "#9cc3ff", SCHVALENA: "#5fd3a5", ZAMITNUTA: "#ff8a7a", NAHRAZENA: "#a3a7ad",
    NEVYBRANA: "#a3a7ad", NEREALIZOVANA: "#ff8a7a" };
  const primarni = nextStatuses.find((s) => s === "ODESLANA" || s === "SCHVALENA");
  const ostatniStavy = nextStatuses.filter((s) => s !== primarni);
  const lzeSmazat = spravce && !verze_seznam.some((vz) => vz.status === "SCHVALENA");
  const lzePrepocet = spravce && je_aktualni && !["SCHVALENA", "NAHRAZENA"].includes(verze.status);
  // Marže po slevě, jen z položek se známým nákupem (vlastní práce, doprava
  // a položky bez nákupu se nepočítají). Subdodavatel nákup nevidí, marži taky ne.
  const { zisk, marze_pct: marzePct } = totals;
  const sazbyText = totals.rekapitulace.map((rk) => `${rk.sazba} %`).join(" + ") || `${defaultSazba} %`;
  // Po změně nabídky (třeba po návratu na rozpracovanou) by starý náhled klamal —
  // tlačítko pak rovnou vytvoří nové PDF stejného druhu.
  const pdfUlozene = meta.pdf?.s_cenami || meta.pdf?.bez_cen;
  const pdfZastarale = !!pdfUlozene && !pdfUlozene.aktualni;
  const otevritPdfNahled = () => {
    if (pdfBusy) return;   // kulaté tlačítko na telefonu `disabled` nezná — dvojí PDF ne
    if (pdfUlozene && pdfUlozene.aktualni) window.open(`${API_BASE}${pdfUlozene.url}`, "_blank", "noopener");
    else exportPdf(pdfUlozene ? !!meta.pdf?.s_cenami : true);
  };
  // Štítek: nevybraná varianta / nerealizovaná objednávka místo holého „Zamítnutá“ / „Schválená“.
  const stavZobrazit = nStav({ status: verze.status, nevybrana: meta.varianty?.nevybrana,
    nerealizovana: verze.status === "SCHVALENA" && meta.varianty?.technologie_ztracena });
  const zamcenoText = meta.varianty?.nevybrana
    ? `Klient vybral jinou variantu${meta.varianty.schvalena_jina ? ` (${meta.varianty.schvalena_jina})` : ""}. Tahle zůstává pro přehled.`
    : verze.status === "SCHVALENA" && meta.varianty?.technologie_ztracena
      ? "Nabídka byla schválená, ale technologie se nerealizovala (je označená jako ztracená)."
      : ZAMCENO[verze.status];
  // Prázdná rozpracovaná nabídka: nejdřív obsah. Místo PDF a „Dokončit“ je hlavní
  // akcí import nabídky dodavatele; jakmile je v nabídce položka, vrátí se PDF.
  const prazdna = editable && rows.length === 0;
  const importAkce = { text: "Importovat PDF / Excel / CSV", ikona: FileUp, onClick: () => setImportOpen(true), testid: "import-hlava" };
  const stavAkce = (s) => ({ text: statusBtn[s].label, ikona: statusBtn[s].icon, onClick: () => changeStatus(s), disabled: busy, testid: `status-${s}` });
  const sekceKarta = (sec) => {
    const radky = rowsByType(sec.typ);
    return (
      <KartaSekce key={sec.typ} testid={`sekce-${sec.typ}`} titul={sec.label} pocet={radky.length || null}
        akce={<span className="amount t-telo md:text-[15px] font-800 text-ink">{fmtKc(filtrEtapa ? radky.reduce((a, rw) => a + radekCelkem(rw), 0) : totals[sec.key])} Kč</span>}>
        {radky.length > 0 && !uzky && (
          <div className={`grid ${skryt ? (sEtapami ? COLS_SUB_E : COLS_SUB) : (sEtapami ? COLS_E : COLS)} gap-2 px-3 md:px-5 pb-2 label-up text-muted`}>
            {sEtapami && <span>Et.</span>}
            {editable ? (
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="h-4 w-4 accent-ink" aria-label="Označit všechny řádky sekce" data-testid={`sekce-vyber-${sec.typ}`}
                  checked={radky.length > 0 && radky.every((rw) => vybrane[rw.id])}
                  onChange={(e) => setVybrane((v) => ({ ...v, ...Object.fromEntries(radky.map((rw) => [rw.id, e.target.checked])) }))} />
                Položka
              </label>
            ) : <span>Položka</span>}
            {skryt
              ? <><span className="text-right">Množ.</span><span className="text-right">Prodej</span><span className="text-right">Celkem</span><span className="text-right">DPH</span></>
              : <><span className="text-right">Množ.</span>
                <span className="text-right">Nákup</span>
                <span className="text-center" title="Sleva z prodeje → nákup (prodej zůstane). Sleva 30 % = marže 42,9 %.">Sleva</span>
                <span className="text-right">Prodej</span>
                <span className="text-center" title="Marže z nákupu → prodej (nákup zůstane)">Marže</span><span className="text-right">Celkem</span><span className="text-right">DPH</span></>}
          </div>
        )}
        {radky.length === 0 && <div className="px-4 md:px-5 pb-3 t-meta text-muted">Zatím žádné položky.</div>}
        {razeni && uzky && editable
          ? radky.map((rw, i) => <RadekPoradi key={rw.id} r={rw} prvni={i === 0} posledni={i === radky.length - 1} onPosun={posun(sec.typ)} />)
          : radky.map((rw) => <Row key={`${rw.id}-${loadKey}`} r={rw} editable={editable} skryt={skryt} mobil={uzky}
          etapy={sEtapami} dphNabidky={defaultSazba} vybrano={!!vybrane[rw.id]} onVyber={prepniVyber} vybirani={vybirani}
          tazeni={editable && !uzky && !filtrEtapa && radky.length > 1 ? tazeniSekce(sec.typ) : null}
          onChange={onRowChange} onFlush={flushRow} onDelete={delRow} />)}
        {editable && !(razeni && uzky) && (
          <div className="flex gap-5 px-4 md:px-5 py-2.5 border-t border-line-soft">
            <Pridat text="Z ceníku" onClick={() => setPicker(sec.typ)} testid={`add-cenik-${sec.typ}`} />
            <button onClick={() => addBlank(sec.typ)} className="tap md:min-h-0 inline-flex items-center gap-1 t-meta font-700 text-sec hover:text-ink" data-testid={`add-blank-${sec.typ}`}>
              <Plus size={15} strokeWidth={2.4} /> Prázdný řádek
            </button>
          </div>
        )}
      </KartaSekce>
    );
  };
  const fakt = (popisek, hodnota, testid, posledni = false) => (
    <div className={`flex items-baseline justify-between gap-3 py-2 ${posledni ? "" : "border-b border-line-soft"}`} data-testid={testid}>
      <span className="t-meta text-muted">{popisek}</span><span className="amount t-telo font-600 text-ink text-right">{hodnota}</span>
    </div>
  );

  return (
    <div data-testid="nabidka-editor" className="flex flex-col gap-4">
      <Drobecky aktualni={nabidka.cislo}
        cesta={[{ nazev: "Nabídky", to: "/nabidky" },
          { nazev: [kontext.pripad_cislo, kontext.pripad_nazev].filter(Boolean).join(" "), to: `/obchod/${kontext.pripad_id}` },
          ...(kontext.technologie_id ? [{ nazev: kontext.technologie_name, to: `/technologie/${kontext.technologie_id}` }] : [])]} />

      <DetailHlava testid="nabidka-hlava" druh={`Nabídka · ${nabidka.cislo}`}
        nazev={upravNazev ? (
          <input autoFocus value={nazevText} maxLength={160} aria-label="Název nabídky" data-testid="nabidka-nazev-input"
            onChange={(e) => setNazevText(e.target.value)}
            onBlur={(e) => (e.currentTarget.dataset.zrus ? setUpravNazev(false) : ulozNazev(e.currentTarget.value))}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
              if (e.key === "Escape") { e.currentTarget.dataset.zrus = "1"; e.currentTarget.blur(); }
            }}
            className="w-[min(640px,80vw)] bg-white/10 rounded-[10px] px-2.5 py-1 text-[16px] md:text-[22px] font-800 text-white outline-none shadow-[inset_0_0_0_1px_rgba(255,255,255,.3)]" />
        ) : nabidka.nazev}
        stitky={<>
          {je_aktualni && !upravNazev && (
            <button type="button" onClick={zacitNazev} aria-label="Přejmenovat nabídku" title="Přejmenovat nabídku" data-testid="nabidka-prejmenovat"
              className="tap md:min-h-0 md:h-7 md:w-7 inline-flex items-center justify-center rounded-full text-muted-3 hover:text-white hover:bg-white/10">
              <Pencil size={15} />
            </button>
          )}
          <StitekTmavy testid="nabidka-stav" barva={B_STAV[stavZobrazit]}>{N_STATUS_LABEL[stavZobrazit] || verze.status}</StitekTmavy>
          {editable && <span className="t-meta text-muted-3" data-testid="save-indicator">{saveState === "saving" ? "Ukládám…" : saveState === "saved" ? "Uloženo" : ""}</span>}
        </>}
        meta={[
          kontext.client_id
            ? <button onClick={() => nav(`/klienti/${kontext.client_id}`)} className="inline-flex items-center gap-1.5 font-600 text-white hover:text-white/80" data-testid="nabidka-klient"><User size={13} />{kontext.client_name}</button>
            : <span className="inline-flex items-center gap-1.5"><User size={13} />{kontext.client_name || "—"}</span>,
          <span className="inline-flex items-center gap-1.5"><Box size={13} />{kontext.technologie_name}</span>,
          <button onClick={() => nav(`/obchod/${kontext.pripad_id}`)} className="inline-flex items-center gap-1.5 hover:text-white" data-testid="nabidka-pripad"><Briefcase size={13} />{kontext.pripad_nazev || kontext.pripad_cislo}</button>,
          <span className="inline-flex items-center gap-1.5"><CalendarDays size={13} />Platnost {verze.platnost_dnu} dní</span>,
        ]}
        pod={verze_seznam.length > 1 && (
          <div className="flex items-center gap-2 mt-1" data-testid="verze-chips">
            <span className="label-up text-muted-3">Verze</span>
            <span className="inline-flex gap-0.5 p-[3px] rounded-full bg-white/[.08]">
              {verze_seznam.map((vz) => {
                const aktivni = vz.verze === verze.verze;
                return (
                  <button key={vz.id} onClick={() => setViewVerze(vz.verze === meta.aktualni_verze ? null : vz.verze)} data-testid={`verze-chip-${vz.verze}`}
                    title={vz.verze === meta.aktualni_verze ? "Aktuální verze" : "Starší verze — jen ke čtení"}
                    className={`h-8 md:h-[26px] px-3 rounded-full text-[12px] font-700 ${aktivni ? "bg-white text-ink" : "text-[#b9bdc3] hover:text-white"}`}>
                    V{vz.verze}
                  </button>
                );
              })}
            </span>
          </div>
        )}
        vedlejsi={prazdna ? [] : [
          meta.pdf?.s_cenami || meta.pdf?.bez_cen
            ? { text: pdfZastarale ? "Aktualizovat PDF" : "Náhled PDF", prvek: <PdfNahledTlacitko pdf={meta.pdf} tmavy onExport={exportPdf} busy={pdfBusy} /> }
            : { text: pdfBusy ? "Připravuji PDF…" : "Exportovat PDF", ikona: FileDown, onClick: () => exportPdf(true), disabled: pdfBusy, testid: "pdf-export-hlava" },
        ]}
        hlavni={prazdna ? importAkce : primarni ? stavAkce(primarni) : null}
        vice={[
          ...(prazdna ? [] : [
            { text: "Vytvořit nové PDF s cenami", ikona: FileDown, onClick: () => exportPdf(true), disabled: pdfBusy, testid: "pdf-s-cenami" },
            { text: "Vytvořit nové PDF bez cen položek", ikona: FileDown, onClick: () => exportPdf(false), disabled: pdfBusy, testid: "pdf-bez-cen" },
          ]),
          ...ostatniStavy.map((s) => ({ ...stavAkce(s), nebezpecne: s === "ZAMITNUTA" })),
          ...(spravce ? [
            { text: "Uložit jako šablonu", ikona: Save, onClick: () => setSablonyDlg({ typ: "sablona" }), testid: "nabidka-ulozit-sablonu" },
            { text: "Použít pro jiného klienta", ikona: Copy, onClick: () => setSablonyDlg({ typ: "kopie" }), testid: "nabidka-pro-jineho" },
          ] : []),
          ...(lzeDoCeniku && rows.length ? [{ text: "Uložit ceny do ceníku", ikona: PackagePlus, onClick: () => otevritDoCeniku({}), testid: "do-ceniku-open" }] : []),
          ...(lzePrepocet ? [{ text: "Přepočítat na dnešní ceník", ikona: RefreshCw, onClick: () => setRecalcOpen(true), testid: "prepocet-open" }] : []),
          ...(editable && !prazdna ? [{ text: "Importovat (Excel / PDF)", ikona: FileUp, onClick: () => setImportOpen(true), testid: "import-open" }] : []),
          ...(je_aktualni ? [{ text: "Přejmenovat nabídku", ikona: Pencil, onClick: zacitNazev, testid: "nabidka-prejmenovat-menu" }] : []),
          { text: "Historie importů", ikona: History, onClick: otevritHistorii, testid: "historie-importu-open" },
          ...(lzeVratitSchvaleni ? [{ text: "Vrátit schválení", ikona: RotateCcw, onClick: vratitSchvaleni, disabled: busy, testid: "vratit-schvaleni" }] : []),
          ...(lzeSmazat ? [{ text: "Smazat nabídku", ikona: Trash2, nebezpecne: true, onClick: smazatNabidku, testid: "nabidka-smazat" }] : []),
        ]}
        cta={prazdna ? [
          { text: "Importovat", ikona: FileUp, onClick: () => setImportOpen(true), testid: "cta-import" },
          { text: "Položka", ikona: Plus, onClick: () => setPicker("MATERIAL"), testid: "cta-polozka" },
        ] : [
          ...(primarni ? [{ text: primarni === "ODESLANA" ? "Dokončit" : "Schválit", ikona: statusBtn[primarni].icon, onClick: () => changeStatus(primarni), testid: `cta-status-${primarni}` }] : []),
          { text: pdfBusy ? "Připravuji PDF…" : pdfZastarale ? "Aktualizovat PDF" : "Náhled PDF", ikona: pdfZastarale ? RefreshCw : Eye, onClick: otevritPdfNahled, disabled: pdfBusy, testid: "cta-pdf" },
          ...(editable ? [{ text: "Položka", ikona: Plus, onClick: () => setPicker("MATERIAL"), testid: "cta-polozka" }] : []),
        ]}
        dlazdice={[
          { popisek: totals.sleva_kc ? "Základ po slevě" : "Základ bez DPH", hodnota: fmtKc(totals.zaklad), jednotka: "Kč",
            pod: totals.sleva_kc ? `sleva ${fmtKc(totals.sleva_kc)} Kč` : "materiál · práce · doprava", testid: "zaklad-celkem" },
          { popisek: "DPH", hodnota: fmtKc(totals.dph), jednotka: "Kč", pod: verze.rezim_dph === "PRENESENA_POVINNOST" ? "daň odvede zákazník" : `sazba ${sazbyText}`, testid: "dph-celkem" },
          { popisek: "Celkem s DPH", hodnota: fmtKc(totals.celkem_s_dph), jednotka: "Kč", pod: "pro klienta", zvyraznit: "accent", testid: "celkem-s-dph" },
          // Zisk v Kč a v % z prodeje — „marže“ na řádku je z nákupu, ať se to neplete.
          ...(!skryt ? [{ popisek: totals.sleva_kc ? "Zisk po slevě" : "Zisk", hodnota: fmtKc(zisk), jednotka: "Kč",
            zvyraznit: zisk < 0 ? "danger" : "#5fd3a5", pod: marzePct == null ? "bez DPH" : `${String(marzePct).replace(".", ",")} % z prodeje, bez DPH`, testid: "nabidka-marze" }] : []),
        ]} />

      {!je_aktualni && (
        <div className="rounded-[16px] bg-[#fdf3e2] text-[#8a5300] px-4 py-3 t-meta font-600 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="archiv-banner">
          <Info size={16} className="shrink-0" />
          <span>Prohlížíš starší verzi V{verze.verze} — jen ke čtení.</span>
          <button onClick={() => setViewVerze(null)} className="tap md:min-h-0 md:ml-auto font-700 text-accent hover:text-accent-hover" data-testid="zpet-aktualni">Zpět na V{meta.aktualni_verze}</button>
        </div>
      )}
      {err && <div className="t-meta text-danger" data-testid="nabidka-chyba">{err}</div>}
      {!editable && je_aktualni && !verze.archiv && zamcenoText && (
        <div className="rounded-[16px] bg-white border border-line px-4 py-3 t-meta text-sec flex items-start gap-2.5" data-testid="zamceno-info">
          <Lock size={15} className="shrink-0 mt-0.5 text-muted" />{zamcenoText}
        </div>
      )}
      {verze.rezim_dph === "PRENESENA_POVINNOST" && (
        <div className="rounded-[16px] bg-[#fff4e6] border border-accent/40 t-meta text-ink px-4 py-3" data-testid="prenesena-banner">
          Režim přenesené daňové povinnosti — na dokladu bude uvedeno <strong>„daň odvede zákazník“</strong> (§ 92e ZDPH).
        </div>
      )}

      {/* Položky přes celou šířku (všechny sloupce na jednom řádku), panel „Pod čarou“
          s Nastavením pod nimi v jedné kartě — hlavní čísla jsou stejně v tmavé hlavičce nahoře. */}
      <div className="grid grid-cols-1 gap-4">
        <div className="flex flex-col gap-4 min-w-0">
          {/* Řádek nad položkami: etapy (Zobrazit …). */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="nad-polozkami">
          {sEtapami ? (
            <div className="flex items-center gap-2 flex-wrap min-w-0" data-testid="etapy-filtr">
              <span className="t-meta text-muted">Zobrazit</span>
              <span className="inline-flex gap-0.5 p-[3px] rounded-full bg-white border border-line max-w-full overflow-x-auto">
                {[0, ...Array.from({ length: Math.min(3, maxEtapa + (editable && maxEtapa < 3 ? 1 : 0)) }, (_, i) => i + 1)].map((e) => (
                  <button key={e} type="button" onClick={() => setFiltrEtapa(e)} data-testid={`etapy-filtr-${e}`}
                    className={`tap md:min-h-0 h-10 md:h-8 px-3.5 rounded-full t-meta font-700 whitespace-nowrap inline-flex items-center gap-1.5 ${filtrEtapa === e ? "bg-ink text-white" : "text-sec hover:text-ink"}`}>
                    {e > 0 && <span className="h-[7px] w-[7px] rounded-full" style={{ background: ETAPA_BARVA[e] }} />}
                    {e ? ETAPA_NAZEV[e] : "Celá nabídka"}
                  </button>
                ))}
              </span>
              <span className="t-meta text-muted inline-flex items-center gap-1" title="Etapu položky změníš klepnutím na štítek vlevo (— / I / II / III). Nová položka se přidá do zobrazené etapy.">
                <Info size={13} /> štítek u položky = etapa
              </span>
            </div>
          ) : editable && (
            <button type="button" onClick={() => setEtapyZap(true)} data-testid="etapy-zapnout"
              className="tap md:min-h-0 inline-flex items-center gap-1.5 t-meta font-700 text-sec hover:text-ink">
              <Plus size={14} strokeWidth={2.4} /> Rozdělit na etapy
            </button>
          )}
          </div>
          {editable && uzky && rows.length > 0 && (
            <div className="flex flex-wrap gap-x-5">
              {!razeni && (
                <button type="button" onClick={() => { setVybirani((v) => !v); setVybrane({}); }} data-testid="vybirani-prepnout"
                  className="tap inline-flex items-center gap-1.5 t-meta font-700 text-sec hover:text-ink">
                  <Check size={14} strokeWidth={2.4} /> {vybirani ? "Zrušit vybírání" : "Vybrat více položek"}
                </button>
              )}
              {rows.length > 1 && (
                <button type="button" onClick={async () => {
                  await Promise.all(Object.keys(pending.current).map((rid) => flushRow(rid)));
                  setRazeni((v) => !v); setVybirani(false); setVybrane({});
                }} data-testid="razeni-prepnout"
                  className={`tap inline-flex items-center gap-1.5 t-meta font-700 ${razeni ? "text-accent" : "text-sec hover:text-ink"}`}>
                  {razeni ? <Check size={14} strokeWidth={2.4} /> : <ArrowUpDown size={14} strokeWidth={2.4} />} {razeni ? "Hotovo" : "Změnit pořadí"}
                </button>
              )}
            </div>
          )}
          {SECTIONS.map(sekceKarta)}
          {editable && vybraneIds.length > 0 && (
            <div className="sticky z-30 bottom-[calc(84px+env(safe-area-inset-bottom))] md:bottom-4" data-testid="hromadna-lista">
              <div className="rounded-[18px] bg-ink text-white shadow-panel px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2.5">
                <span className="flex items-center gap-3 w-full md:w-auto">
                  <span className="t-telo font-800 whitespace-nowrap" data-testid="hromadna-pocet">Vybráno {vybraneIds.length}</span>
                  {vybraneIds.length < rows.length && (
                    <button type="button" onClick={() => setVybrane(Object.fromEntries(rows.map((rw) => [rw.id, true])))}
                      data-testid="hromadna-vse" className="tap md:min-h-0 t-meta font-700 text-white underline underline-offset-2 hover:text-white/80">
                      Vybrat vše ({rows.length})
                    </button>
                  )}
                  <button type="button" onClick={() => setVybrane({})} className="tap ml-auto md:hidden t-meta font-700 text-muted-3">Zrušit výběr</button>
                </span>
                {!skryt && (
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span className="t-meta text-muted-3">Marže</span>
                    {[25, 30, 35].map((m) => (
                      <button key={m} type="button" disabled={busy} onClick={() => hromadne({ marze_pct: m })} data-testid={`hromadna-marze-${m}`}
                        className="tap md:min-h-0 h-10 md:h-8 px-2.5 md:px-3 rounded-full bg-white/10 hover:bg-white/20 t-meta font-700">{m} %</button>
                    ))}
                    <input value={hromadnaMarze} onChange={(e) => setHromadnaMarze(e.target.value)} inputMode="decimal" placeholder="jiná"
                      onKeyDown={(e) => e.key === "Enter" && vlastniMarze()} aria-label="Vlastní marže v %" data-testid="hromadna-marze-vlastni"
                      className="amount w-16 h-10 md:h-8 text-[16px] md:text-[12.5px] text-center bg-white/10 rounded-full outline-none text-white placeholder:text-muted-3" />
                    {hromadnaMarze.trim() !== "" && (
                      <button type="button" disabled={busy} onClick={vlastniMarze} data-testid="hromadna-marze-nastavit" aria-label="Nastavit marži" title="Nastavit marži"
                        className="tap md:min-h-0 h-10 w-10 md:h-8 md:w-8 inline-flex items-center justify-center rounded-full bg-white text-ink disabled:opacity-50"><Check size={16} strokeWidth={2.6} /></button>
                    )}
                  </span>
                )}
                {!skryt && (
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span className="t-meta text-muted-3" title="Nákup = prodej − sleva, prodej zůstane">Sleva</span>
                    <input value={hromadnaSleva} onChange={(e) => setHromadnaSleva(e.target.value)} inputMode="decimal" placeholder="%"
                      onKeyDown={(e) => e.key === "Enter" && nastavSlevu()} aria-label="Sleva z prodeje v %" data-testid="hromadna-sleva"
                      className="amount w-16 h-10 md:h-8 text-[16px] md:text-[12.5px] text-center bg-white/10 rounded-full outline-none text-white placeholder:text-muted-3" />
                    {hromadnaSleva.trim() !== "" && (
                      <button type="button" disabled={busy} onClick={nastavSlevu} data-testid="hromadna-sleva-nastavit" aria-label="Nastavit slevu" title="Nákup = prodej − sleva"
                        className="tap md:min-h-0 h-10 w-10 md:h-8 md:w-8 inline-flex items-center justify-center rounded-full bg-white text-ink disabled:opacity-50"><Check size={16} strokeWidth={2.6} /></button>
                    )}
                  </span>
                )}
                {!skryt && (
                  // Opak „Ceny do ceníku“: nákup a prodej řádků znovu podle ceníku (jako při vložení položky).
                  <button type="button" disabled={busy} onClick={() => hromadne({ z_ceniku: true })} data-testid="hromadna-z-ceniku"
                    title="Nákup, marže a prodejní cena podle ceníku; řádky mimo ceník zůstanou"
                    className="tap md:min-h-0 h-10 md:h-8 px-3 rounded-full bg-white/10 hover:bg-white/20 t-meta font-700 inline-flex items-center gap-1.5">
                    <RefreshCw size={14} /> Ceny z ceníku
                  </button>
                )}
                {lzeDoCeniku && (
                  <button type="button" disabled={busy} onClick={() => otevritDoCeniku({ ids: vybraneIds })} data-testid="hromadna-do-ceniku"
                    className="tap md:min-h-0 h-10 md:h-8 px-3 rounded-full bg-white/10 hover:bg-white/20 t-meta font-700 inline-flex items-center gap-1.5">
                    <PackagePlus size={14} /> Ceny do ceníku
                  </button>
                )}
                {sEtapami && (
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span className="t-meta text-muted-3">Etapa</span>
                    {[1, 2, 3, 0].map((e) => (
                      <button key={e} type="button" disabled={busy} onClick={() => hromadne({ etapa: e })} data-testid={`hromadna-etapa-${e}`}
                        className="tap md:min-h-0 h-10 md:h-8 min-w-[40px] px-2.5 rounded-full bg-white/10 hover:bg-white/20 t-meta font-700">{ETAPA_RIM[e]}</button>
                    ))}
                  </span>
                )}
                <button type="button" onClick={() => setVybrane({})} data-testid="hromadna-zrusit"
                  className="hidden md:inline-flex md:ml-auto t-meta font-700 text-muted-3 hover:text-white">Zrušit výběr</button>
              </div>
            </div>
          )}

          <KartaSekce testid="sekce-doprava" titul="Doprava" telo
            akce={<span className="amount t-telo md:text-[15px] font-800 text-ink">{fmtKc(totals.doprava_celkem)} Kč</span>}>
            <div className="t-meta text-muted mb-3">Doprava se daní sazbou hlavního plnění.</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[["vzdalenost_km", "Vzdálenost (km)"], ["pocet_jizd", "Počet jízd"], ["sazba_kc_km", "Sazba Kč/km"]].map(([k, l]) => (
                <label key={k} className="block min-w-0"><span className="label-up text-muted block mb-1">{l}</span>
                  <input disabled={!editable} type="number" inputMode="decimal" className="field amount w-full min-w-0" value={verze.doprava?.[k] ?? 0} onChange={(e) => patchDoprava(k, e.target.value)} data-testid={`doprava-${k}`} /></label>
              ))}
              <label className="block min-w-0"><span className="label-up text-muted block mb-1">Sazba DPH</span>
                <DphPicker value={totals.doprava_dph_sazba_pct} disabled={!editable} testid="doprava-dph" compact={false}
                  onChange={(v) => patchDoprava("dph_sazba_pct", v)} />
              </label>
            </div>
          </KartaSekce>
        </div>

        <KartaSekce testid="nabidka-totals" titul="Pod čarou" telo
            akce={<button onClick={() => setShowHelp((v) => !v)} aria-label="Sazby DPH" className="tap md:min-h-0 inline-flex items-center gap-1 t-meta font-600 text-muted hover:text-ink" data-testid="dph-help-toggle"><Info size={14} /> DPH</button>}>
            {showHelp && <div className="t-meta text-muted mb-3" data-testid="dph-help">{DPH_HELP}</div>}
            {totals.etapy.length > 0 && totals.etapy.map((e) => (
              <div key={e.etapa} className="py-2 border-b border-line-soft" data-testid={`souhrn-etapa-${e.etapa}`}>
                <div className="flex items-baseline gap-2">
                  <span className="h-2 w-2 rounded-[2px] shrink-0 self-center" style={{ background: ETAPA_BARVA[e.etapa] }} />
                  <span className="t-meta text-sec">{e.nazev}</span>
                  <span className="amount t-telo font-600 text-ink ml-auto">{fmtKc(e.celkem)} Kč</span>
                </div>
                <div className="t-meta text-muted pl-4">
                  {[["mat.", e.material], ["práce", e.prace], ["ost.", e.ostatni]].filter(([, v]) => v).map(([l, v]) => `${l} ${fmtKc(v)}`).join(" · ")}
                </div>
              </div>
            ))}
            {totals.etapy.length === 0 && <>
              {fakt("Materiál", `${fmtKc(totals.mezisoucet_material)} Kč`)}
              {fakt("Práce", `${fmtKc(totals.mezisoucet_prace)} Kč`)}
              {fakt("Ostatní", `${fmtKc(totals.mezisoucet_ostatni)} Kč`)}
            </>}
            {totals.doprava_celkem > 0 && fakt("Doprava", `${fmtKc(totals.doprava_celkem)} Kč`)}
            {fakt("Součet bez DPH", `${fmtKc(totals.soucet)} Kč`, "souhrn-soucet")}

            <div className="py-2.5 border-b border-line-soft" data-testid="sleva">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="t-telo font-700 text-ink">Sleva</span>
                <span className="inline-flex gap-0.5 p-[3px] rounded-full bg-inputbg">
                  {[["KC", "Kč"], ["PCT", "%"]].map(([t, l]) => (
                    <button key={t} type="button" disabled={!editable} onClick={() => patchSleva({ typ: t, hodnota: 0 })} data-testid={`sleva-typ-${t}`}
                      className={`h-9 md:h-[26px] px-3 rounded-full t-meta font-700 disabled:opacity-60 ${(verze.sleva?.typ || "KC") === t ? "bg-ink text-white" : "text-sec"}`}>{l}</button>
                  ))}
                </span>
                <span className="ml-auto w-[120px]">
                  <CisloInput velke disabled={!editable} value={verze.sleva?.hodnota || ""} testid="sleva-hodnota"
                    onCommit={(v) => patchSleva({ hodnota: Math.max(0, v) })} />
                </span>
              </div>
              {totals.sleva_kc > 0 && (
                <div className="flex t-meta mt-1">
                  <span className="text-muted">{verze.sleva?.typ === "PCT" ? `z ${fmtKc(totals.soucet)} Kč` : `= ${String(Math.round((totals.sleva_kc / totals.soucet) * 10000) / 100).replace(".", ",")} % ze součtu`}</span>
                  <span className="amount ml-auto text-danger" data-testid="sleva-kc">−{fmtKc(totals.sleva_kc)} Kč</span>
                </div>
              )}
            </div>
            {totals.sleva_kc > 0 && fakt("Základ po slevě", `${fmtKc(totals.zaklad)} Kč`, "souhrn-zaklad")}

            <div className="py-2.5 border-b border-line-soft" data-testid="dph-nabidky">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="t-telo font-700 text-ink">DPH</span>
                <span className="inline-flex gap-0.5 p-[3px] rounded-full bg-inputbg ml-auto">
                  {DPH_PREDVOLBY.map((v) => (
                    <button key={v} disabled={!editable || prenesena} onClick={() => setDefaultDph(v)} data-testid={`dph-${v}`}
                      className={`h-10 md:h-[28px] px-3.5 rounded-full text-[13px] md:text-[12px] font-700 disabled:opacity-60 ${defaultSazba === v ? "bg-ink text-white" : "text-sec hover:text-ink"}`}>{v} %</button>
                  ))}
                </span>
                <input type="number" min={0} max={100} disabled={!editable || prenesena} placeholder="jiná" inputMode="decimal"
                  key={`dph-${defaultSazba}`} defaultValue={DPH_PREDVOLBY.includes(defaultSazba) ? "" : defaultSazba}
                  onBlur={(e) => { const n = Number(e.target.value); if (e.target.value !== "" && validDph(n) && n !== defaultSazba) setDefaultDph(n); }}
                  onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                  className="amount w-16 h-10 md:h-8 text-[16px] md:text-[12.5px] text-center bg-inputbg border border-line-strong rounded-full outline-none"
                  data-testid="dph-jina" />
              </div>
              <div className="t-meta text-muted mt-1">
                {prenesena ? "Přenesená daňová povinnost — všechny položky 0 %." : "Platí pro všechny položky i dopravu. Výjimku nastavíš u položky."}
              </div>
            </div>
            {totals.rekapitulace.length > 1 && totals.rekapitulace.map((rk) => (
              <div key={rk.sazba} className="flex items-baseline justify-between gap-3 py-2 border-b border-line-soft" data-testid={`rekap-${rk.sazba}`}>
                <span className="t-meta text-muted">Základ {rk.sazba} % · DPH</span>
                <span className="amount t-meta text-sec text-right">{fmtKc(rk.zaklad)} · {fmtKc(rk.dph)} Kč</span>
              </div>
            ))}
            {fakt(totals.rekapitulace.length > 1 ? "DPH celkem" : `DPH ${totals.rekapitulace[0]?.sazba ?? defaultSazba} %`, `${fmtKc(totals.dph)} Kč`)}
            <div className="flex items-baseline justify-between gap-3 pt-3">
              <span className="t-telo font-700 text-ink">Celkem s DPH</span>
              <span className="amount text-[18px] font-800 text-ink" data-testid="rozpis-celkem-s-dph">{fmtKc(totals.celkem_s_dph)} Kč</span>
            </div>
            {!skryt && (
              <div className={`mt-3 rounded-[12px] px-3 py-2.5 t-meta ${zisk < 0 ? "bg-[#fdeeec]" : "bg-success-bg"}`} data-testid="pod-carou-marze">
                <div className="flex items-center gap-2">
                  <span className={`font-700 ${zisk < 0 ? "text-danger" : "text-success"}`}>
                    {totals.sleva_kc ? "Zisk po slevě" : "Zisk"} {fmtKc(zisk)} Kč
                  </span>
                  {marzePct != null && <span className="text-sec">· {String(marzePct).replace(".", ",")} % z prodeje</span>}
                </div>
                {totals.bez_nakupu > 0 && (
                  <div className="text-muted mt-0.5">{totals.bez_nakupu} {totals.bez_nakupu === 1 ? "položka" : totals.bez_nakupu < 5 ? "položky" : "položek"} bez nákupu se do marže nepočítá.</div>
                )}
              </div>
            )}
            {/* Nastavení nabídky ve stejné kartě (přes celou šířku): režim DPH, platnost, verze. */}
            <div className="mt-4 pt-4 border-t border-line-soft grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 items-start" data-testid="nabidka-nastaveni">
              <div className="min-w-0 col-span-2 md:col-span-1">
                <span className="label-up text-muted block mb-1">Režim DPH</span>
                <Vyber disabled={!editable} value={verze.rezim_dph || "STANDARDNI"} data-testid="rezim-dph"
                  onChange={(e) => setRezimDph(e.target.value)}
                  className="w-full min-w-0 field text-[16px] md:text-[12.5px]">
                  <option value="STANDARDNI">Standardní</option>
                  <option value="PRENESENA_POVINNOST">Přenesená daňová povinnost</option>
                  <option value="OSVOBOZENO">Osvobozeno od DPH</option>
                  <option value="MIMO_TUZEMSKO">Plnění mimo tuzemsko</option>
                </Vyber>
                {(verze.rezim_dph === "PRENESENA_POVINNOST") && (
                  <div className="mt-2 t-meta text-muted" data-testid="prenesena-info">
                    Všechny řádky mají 0 %. Na nabídce bude uvedeno <strong>„daň odvede zákazník"</strong>.
                    Použij u stavebních a montážních prací pro plátce DPH (§ 92e).
                  </div>
                )}
              </div>
              <div className="min-w-0">
                <span className="label-up text-muted block mb-1">Platnost</span>
                <div className="t-telo font-700 text-ink md:h-[38px] md:flex md:items-center" data-testid="nabidka-platnost">{verze.platnost_dnu} dní</div>
              </div>
              <div className="min-w-0">
                <span className="label-up text-muted block mb-1">Verze</span>
                <div className="t-telo font-700 text-ink md:h-[38px] md:flex md:items-center" data-testid="nabidka-verze">
                  V{verze.verze}{je_aktualni ? "" : ` (aktuální V${meta.aktualni_verze})`}
                </div>
              </div>
            </div>
          </KartaSekce>
      </div>

      {picker && <CenikPicker kategorieId={kategorie_id} onAdd={addFromCenik} onClose={() => setPicker(null)} />}
      {importOpen && <ImportWizard vid={vid} bezCeniku={skryt} dph={prenesena ? 0 : defaultSazba} onClose={() => setImportOpen(false)} onDone={load}
        nabidka={nabidka} onNazev={(n) => setMeta((m) => ({ ...m, nabidka: { ...m.nabidka, nazev: n } }))} />}
      {historie && (
        <Okno titulek={historie.detail ? `Import ${historie.detail.soubor || ""}` : "Historie importů"} testid="historie-importu"
          sirka={historie.detail ? "md:max-w-[900px]" : "md:max-w-[560px]"}
          onClose={() => setHistorie(null)}
          paticka={historie.detail && <>
            <button type="button" onClick={() => setHistorie((h) => ({ ...h, detail: null }))} className="btn-ghost tap md:min-h-0 h-10" data-testid="historie-zpet">Zpět na seznam</button>
            {historie.detail.lze_vratit && (
              <button type="button" onClick={() => vratitImportZHistorie(historie.detail)} className="btn-ghost tap md:min-h-0 h-10 text-danger" data-testid="historie-vratit">
                <RotateCcw size={15} /> Vrátit import
              </button>
            )}
          </>}>
          {historie.detail ? (
            <>
              <div className="t-meta text-muted">
                {fmtDateTime(historie.detail.created_at)} · {historie.detail.radku} položek
                {historie.detail.zmeny_ceniku.nove.length ? ` · ${historie.detail.zmeny_ceniku.nove.length} nových v ceníku` : ""}
                {historie.detail.zmeny_ceniku.ceny.length ? ` · ${historie.detail.zmeny_ceniku.ceny.length} oprav cen v ceníku` : ""}
                {historie.detail.vraceno_at ? ` · vráceno ${fmtDateTime(historie.detail.vraceno_at)}` : ""}
              </div>
              {historie.detail.radky.length
                ? <PrehledImportu radky={historie.detail.radky} bezCeniku={skryt} />
                : <div className="t-meta text-muted" data-testid="historie-bez-prehledu">U staršího importu se přehled položek neukládal.</div>}
            </>
          ) : historie.items.length === 0 ? (
            <div className="t-meta text-muted" data-testid="historie-prazdna">Do téhle verze nabídky se zatím nic neimportovalo.</div>
          ) : historie.items.map((d) => (
            <button key={d.id} type="button" onClick={() => detailImportu(d.id)} data-testid={`historie-davka-${d.id}`}
              className="w-full text-left rounded-[12px] border border-line hover:border-line-strong px-3.5 py-3 flex items-center gap-3">
              <span className="min-w-0 flex-1 flex flex-col gap-0.5">
                <span className="t-telo font-700 text-ink truncate">{d.soubor || "Import"}</span>
                <span className="t-meta text-muted">
                  {fmtDateTime(d.created_at)} · {d.radku} položek
                  {d.novych_v_ceniku ? ` · ${d.novych_v_ceniku} nových v ceníku` : ""}{d.zmen_cen ? ` · ${d.zmen_cen} oprav cen` : ""}
                </span>
              </span>
              {d.vraceno_at
                ? <span className="t-meta font-700 text-muted shrink-0">Vráceno</span>
                : <span className="t-meta font-700 text-accent shrink-0">Zobrazit</span>}
            </button>
          ))}
        </Okno>
      )}
      {recalcOpen && <PrepocetPanel nid={nabidka.id} onClose={() => setRecalcOpen(false)}
        onCreated={() => { setRecalcOpen(false); setViewVerze(null); load(); }} />}
      {doCeniku && <DoCenikuDialog vid={vid} ids={doCeniku.ids} dokonceni={!!doCeniku.dokonceni}
        onClose={() => setDoCeniku(null)}
        onHotovo={async (ulozeno) => {
          const dokoncit = doCeniku.dokonceni;
          setDoCeniku(null);
          if (doCeniku.ids) setVybrane({});
          if (dokoncit) await nastavStav("ODESLANA");
          else if (ulozeno) await load();
        }} />}
      <PdfCeka zobrazit={pdfCeka} />
      <PdfPripraveno url={pdfOdkaz} nazev="PDF nabídky" onClose={() => setPdfOdkaz(null)} />
      {sablonyDlg?.typ === "sablona" && <NazevSablonyDialog onClose={() => setSablonyDlg(null)}
        popis="Šablona se uloží k hlavnímu zařízení nabídky i s množstvím, prací a dopravou. Ceny se při použití vezmou aktuální z ceníku."
        onUlozit={async (nazev) => {
          const { data: s } = await api.post(`/api/nabidky/${nabidka.id}/sablona`, { nazev });
          setSablonyDlg(null);
          nav(`/cenik/zarizeni/${s.zarizeni_id}?sablona=${s.id}`);
        }} />}
      {sablonyDlg?.typ === "kopie" && <CilNabidkyDialog titulek="Použít pro jiného klienta" kategorieId={kategorie_id}
        popis="Množství zůstanou, ceny se vezmou aktuální z ceníku. Když už klient nabídku má, přidá se jako nová verze."
        onClose={() => setSablonyDlg(null)}
        onVyber={async (c) => {
          const { data: res } = await api.post(`/api/nabidky/${nabidka.id}/kopie`, { technologie_id: c.technologie_id });
          setSablonyDlg({ typ: "kopieHotovo", res, klient: c.client_name });
        }} />}
      {sablonyDlg?.typ === "kopieHotovo" && (
        <Okno titulek={`Nabídka ${sablonyDlg.res.cislo} pro ${sablonyDlg.klient}`} onClose={() => setSablonyDlg(null)} testid="kopie-hotovo"
          paticka={<Button onClick={() => { const id = sablonyDlg.res.nabidka_id; setSablonyDlg(null); nav(`/nabidky/${id}`); }} data-testid="kopie-otevrit">Otevřít nabídku</Button>}>
          <p className="t-telo text-sec">{sablonyDlg.res.nova_nabidka ? "Nová nabídka je založená." : `Přidáno jako verze ${sablonyDlg.res.verze}.`}</p>
          <ZmenyCen zmeny={sablonyDlg.res.zmeny_cen} />
        </Okno>
      )}
    </div>
  );
}
